'use client';

// The step list and the terminal that plays each step. Autoplay advances the
// steps and loops; a click jumps to a step and plays it from the start. With
// reduced motion there is no autoplay and each step shows its whole output.
import Image from 'next/image';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { advance, APP_NAME, frame, type PlayState, STEPS, TICK_MS, type Tone } from '../how-it-works-script';

const TONE_CLASS: Record<Tone, string> = {
  text: 'text-bnk-text',
  secondary: 'text-bnk-text-secondary',
  tertiary: 'text-bnk-text-tertiary',
  brand: 'text-bnk-brand'
};

const TERMINAL_DOTS = 3;
const NOTIFICATION_ICON = '/images/webclip.png';
const NOTIFICATION_ICON_SIZE = 40;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const subscribeToMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};
const prefersReducedMotion = () => window.matchMedia(REDUCED_MOTION).matches;
// The server renders the animated page; the browser's setting applies after.
const serverPrefersReducedMotion = () => false;

export function HowItWorks() {
  const reducedMotion = useSyncExternalStore(subscribeToMotion, prefersReducedMotion, serverPrefersReducedMotion);
  const autoplay = !reducedMotion;
  const [play, setPlay] = useState<PlayState>({ step: 0, elapsed: 0 });

  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(() => setPlay(advance), TICK_MS);
    return () => clearInterval(timer);
  }, [autoplay]);

  const view = frame(play, autoplay);

  return (
    <div className="mt-12 grid items-stretch gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-3">
        {STEPS.map((step, i) => {
          const active = i === play.step;
          return (
            <button
              aria-pressed={active}
              className={`relative flex flex-col gap-2 overflow-hidden rounded-2xl border px-6 py-5 text-left transition-colors ${
                active ? 'border-bnk-brand/50 bg-bnk-surface-1' : 'border-bnk-stroke-subtle bg-transparent'
              }`}
              key={step.num}
              onClick={() => setPlay({ step: i, elapsed: 0 })}
              type="button"
            >
              <span className="flex items-baseline gap-3">
                <span className="font-mono text-xs tracking-widest text-bnk-text-tertiary">{step.num}</span>
                <span className={`font-clash text-[22px] font-semibold ${active ? 'text-bnk-brand' : 'text-bnk-text'}`}>
                  {step.title}
                </span>
              </span>
              <code className="break-words font-mono text-sm text-bnk-text">{step.command}</code>
              <span className="text-sm text-bnk-text-secondary">{step.description}</span>
              <span
                aria-hidden
                className="absolute bottom-0 left-0 h-0.5 bg-bnk-brand"
                style={{ width: `${view.progress[i] * 100}%` }}
              />
            </button>
          );
        })}
      </div>

      <div className="relative flex min-h-[420px] flex-col overflow-hidden rounded-2xl border border-bnk-stroke-subtle bg-bnk-surface-0">
        <div className="flex items-center gap-2 border-b border-bnk-stroke-subtle px-5 py-3.5">
          {Array.from({ length: TERMINAL_DOTS }, (_, i) => (
            <span aria-hidden className="size-2.5 rounded-full bg-bnk-surface-5" key={i} />
          ))}
          <span className="ml-3 font-mono text-xs text-bnk-text-tertiary">{view.directory}</span>
        </div>
        {/* The bottom padding keeps the text clear of the notification. */}
        <div className="flex flex-1 flex-col gap-1.5 px-6 pb-[104px] pt-6 font-mono text-[15px] leading-6">
          {view.lines.map((line, i) => (
            <div className={`flex gap-3 ${line.gap ? 'mt-2.5' : ''}`} key={i}>
              <span className={`w-3.5 shrink-0 ${TONE_CLASS[line.prefixTone]}`}>{line.prefix}</span>
              <span className={`min-w-0 whitespace-pre-wrap break-words ${TONE_CLASS[line.tone]}`}>
                {line.text}
                <span
                  aria-hidden
                  className={`ml-0.5 inline-block h-[18px] w-[9px] bg-bnk-brand align-[-3px] ${
                    line.cursor ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </span>
            </div>
          ))}
        </div>

        <div
          className={`pointer-events-none absolute inset-x-4 bottom-4 flex justify-center transition duration-200 ${
            view.notification ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
          }`}
        >
          <div className="flex w-full max-w-[380px] items-center gap-3 rounded-2xl bg-bnk-surface-5 px-3.5 py-3">
            <Image
              alt=""
              className="size-10 shrink-0 rounded-[10px]"
              height={NOTIFICATION_ICON_SIZE}
              src={NOTIFICATION_ICON}
              width={NOTIFICATION_ICON_SIZE}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex justify-between gap-2">
                <span className="text-sm font-bold text-bnk-text">{APP_NAME} is ready</span>
                <span className="text-xs text-bnk-text-secondary">now</span>
              </div>
              <span className="text-sm text-bnk-text-secondary">Tap to play it in Bankroll.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
